import { z } from 'zod';

import { renderPromptPlaceholders } from './common.js';
import { CREATIVE_BRIEF_MAX, creativeFormatIdSchema, type CreativeFormatId } from './creative.js';
import type { SceneCategoryCard, ScenePaletteCard, SceneTemplateCard } from './scene-public.js';
import { CreativeType } from './enums.js';
import {
  MODEL_AGE_RANGES,
  MODEL_BODY_TYPES,
  MODEL_GENDERS,
  type ModelAgeRange,
  type ModelBodyType,
  type ModelGender,
} from './studio-models.js';

/**
 * GÖRSEL STÜDYO SÖZLEŞMESİ — Genişleme Adım 8.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 YENİ `CreativeType` AÇILMADI — ve bu bir kısıt değil, bir karar
 * ═══════════════════════════════════════════════════════════════════════════
 * Enum genişletmesi migration ister ve bu adım şemaya dokunmadan koştu. Ama
 * asıl gerekçe semantik: stüdyonun ürettiği şey ZATEN mevcut tiplerden biri.
 *
 *   sanal manken / kıyafet deneme → `AD_IMAGE`  (yeni bir reklam görseli)
 *   banner stüdyosu               → `AD_IMAGE`  (yeni bir reklam görseli)
 *   2K büyütme                    → `RESIZED`   (aynı görselin başka boyutu)
 *
 * Büyütmenin `RESIZED` olması bir kaçamak değil: `CREATIVE_FORMATS` zaten
 * `square_small` (600×600) ile `feed_square` (1080×1080) arasında SAF BİR
 * BOYUT DEĞİŞİKLİĞİNİ `RESIZED` sayıyor. Büyütme aynı işlemin diğer yönü —
 * kompozisyon aynı, piksel sayısı farklı. `IMPROVED` olsaydı yanlış olurdu:
 * o tip "yeniden tasarla" demek ve büyütme hiçbir şeyi yeniden tasarlamıyor.
 *
 * Ayırt edici bilgi (hangi mod, hangi şablon, hangi ürün) `Creative
 * .inputParams.studio` alanında duruyor — video ekseninin `inputParams.video`
 * ile yaptığının aynısı ve aynı gerekçeyle: tip KABA sınıf, girdi İNCE ayrım.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Fiyatlar ÖLÇÜLDÜ — 13 Ağu 2026, fal.ai yayımlanmış fiyat kataloğu
 * ═══════════════════════════════════════════════════════════════════════════
 *   kıyafet deneme  `fal-ai/flux-pro/v1/vto`      ~$0,0475 (2 girdi + 1 çıktı MP)
 *   banner          `fal-ai/nano-banana/edit`      $0,039  / görsel
 *   2K büyütme      `fal-ai/topaz/upscale/image`   $0,08   (24 MP'ye kadar)
 *
 * Fiyatı YAYIMLANMAMIŞ modeller (fashn/tryon, clarity-upscaler, recraft) tam
 * bu yüzden elendi: kredi kademesi ölçülmemiş bir sayıya dayanamaz. `esrgan`
 * ucuz ama "hesaplama saniyesi başına" faturalıyor — sabit bir krediyi
 * değişken bir maliyete bağlamak, uzun süren tek bir işte zarar demekti.
 *
 * Kademe `apps/api/src/creative/creative.cost.ts`te; buradaki sabitler onun
 * girdisi ve fiyatın TEK kaynağı yine `CREDIT_COST_BY_CREATIVE_TYPE`.
 */

// ═══════════════════════════════════════════════════════════════════════════
// Modlar
// ═══════════════════════════════════════════════════════════════════════════

export const STUDIO_MODES = ['tryon', 'banner', 'upscale'] as const;
export type StudioMode = (typeof STUDIO_MODES)[number];
export const studioModeSchema = z.enum(STUDIO_MODES);

/**
 * MOD → KREATİF TİPİ. Tek yön, tek tablo.
 *
 * Servis katmanına dağılmış `mode === 'upscale' ? RESIZED : AD_IMAGE` yazmak
 * aynı sorunun üçüncü cevabı olurdu ve biri bir gün diğerlerinden ayrışırdı.
 */
export const STUDIO_CREATIVE_TYPE: Record<StudioMode, CreativeType> = {
  tryon: CreativeType.AD_IMAGE,
  banner: CreativeType.AD_IMAGE,
  upscale: CreativeType.RESIZED,
};

/**
 * 🔴 MOD → SAĞLAYICI ADI. API ve worker AYNI tablodan okuyor.
 *
 * `Creative.provider` kolonuna API üretimden ÖNCE yazıyor (kart hangi
 * sağlayıcıyla üretildiğini göstersin diye), worker ise üretimden SONRA kendi
 * adını yazıyor. İki taraf iki ayrı sabitten okusaydı aynı kreatif kütüphanede
 * bir ada, detayda başka bir ada sahip olurdu — video tarafında bu tuzak
 * yorumla işaretlenmişti (`provider: 'kling'` notu), burada tabloyla kapatıldı.
 *
 * Değerler MODEL AİLESİ, ürün adı değil: `Creative.provider` sözlüğü
 * ("flux" | "kling" | …) zaten böyle okunuyor.
 */
export const STUDIO_PROVIDER_NAME: Record<StudioMode, string> = {
  tryon: 'flux-vto',
  banner: 'nano-banana',
  upscale: 'topaz',
};

// ═══════════════════════════════════════════════════════════════════════════
// Sanal manken
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Manken tarifi — YALNIZ manken üretildiğinde kullanılıyor.
 *
 * Kullanıcı kendi manken fotoğrafını yüklediyse (`modelAssetKey`) bu alanların
 * hiçbiri okunmuyor: verilen fotoğrafı "düzeltmeye" çalışmak, kullanıcının
 * seçtiği kişiyi değiştirmek olurdu.
 *
 * Kapalı listeler serbest metin yerine bilinçli: manken tarifi doğrudan
 * prompt'a giriyor ve serbest metin hem tahmin edilemeyen çıktı hem de
 * moderasyon yüzeyi demek. Kapalı liste ikisini de kesiyor.
 */
/**
 * 🔴 CİNSİYET / YAŞ / VÜCUT LİSTELERİ ARTIK `studio-models.ts`TE.
 *
 * 21 Ağu 2026'da taşındılar ve yön bilerek bu: manken artık bir stüdyo MODU
 * değil, `prompt` ve `product` sekmelerinin ikisinde de bulunan bir eksen —
 * yani listelerin asıl sahibi kadro dosyası, bu dosya değil. Buradan
 * `re-export` edilmiyor, `index.ts` ikisini de yayıyor: tüketiciler
 * `@mentorivo/shared` barrel'ından okuduğu için hiçbir import değişmedi.
 *
 * Ters yön (kadronun buradan okuması) MÜMKÜN DEĞİLDİ: `creative.ts`
 * kadrodan `presenterParamsSchema`yı DEĞER olarak alıyor, bu dosya da
 * `creative.ts`ten değer alıyor — kadro buradan bir şey import etseydi
 * çalışma zamanında gerçek bir döngü doğardı.
 */

/// Sahne — mankenin durduğu ortam. Ürün fotoğrafının arka planını DEĞİL,
/// üretilen manken görselinin arka planını belirliyor.
export const MODEL_SCENES = [
  'studio-white',
  'studio-neutral',
  'street',
  'indoor-loft',
  'outdoor-nature',
] as const;
export type ModelScene = (typeof MODEL_SCENES)[number];

export const modelBriefSchema = z.object({
  gender: z.enum(MODEL_GENDERS).default('female'),
  ageRange: z.enum(MODEL_AGE_RANGES).default('26-35'),
  bodyType: z.enum(MODEL_BODY_TYPES).default('average'),
  scene: z.enum(MODEL_SCENES).default('studio-neutral'),
});
export type ModelBrief = z.infer<typeof modelBriefSchema>;

export const tryonParamsSchema = z.object({
  /**
   * Yüklenmiş manken fotoğrafının S3 anahtarı.
   *
   * Verilmezse manken ÜRETİLİYOR (`modelBrief`ten) ve ancak ondan sonra
   * deneme çalışıyor — UGC videosundaki sunucu görseli zincirinin aynısı.
   * Bu, "sanal manken" ile "kıyafet deneme"yi tek akışta birleştiren şey:
   * elinde manken fotoğrafı olmayan bir mağaza da deneme üretebiliyor.
   */
  modelAssetKey: z.string().max(400).optional(),
  /**
   * ⚠️ `.default({})` DEĞİL `.prefault({})`.
   *
   * `default` değeri parse'tan SONRA koyuyor, yani `{}` olduğu gibi geçer ve
   * `gender`/`ageRange`/… alanları `undefined` kalırdı — sonra prompt kurucu
   * `undefined` bir cinsiyetle manken tarif etmeye çalışırdı. `prefault` ise
   * değeri parse'tan ÖNCE koyuyor: `{}` iç şemadan geçiyor ve her alan kendi
   * varsayılanıyla doluyor. Tip tarafı da bunu zorluyor (zod 4 `default` için
   * TAM nesne istiyor) — yani hata derlemede çıktı, çalışma zamanında değil.
   */
  modelBrief: modelBriefSchema.prefault({}),
});
export type TryonParams = z.infer<typeof tryonParamsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Banner stüdyosu
// ═══════════════════════════════════════════════════════════════════════════

export const BANNER_TEMPLATE_CATEGORIES = [
  /// Ürünün kendisi kahraman — sade vitrin, tek ürün.
  'product',
  /// İndirim, kampanya, sezon sonu — aciliyet ve rakam taşıyor.
  'promo',
  /// Yeni ürün / koleksiyon duyurusu.
  'launch',
  /// Mevsim, tatil, özel gün.
  'seasonal',
  /// Kanıt: puan, yorum, "en çok satan", garanti.
  'proof',
] as const;
export type BannerTemplateCategory = (typeof BANNER_TEMPLATE_CATEGORIES)[number];

/**
 * Banner yer tutucuları.
 *
 * `video.ts`teki kümeden AYRI ve daha geniş: banner'ın taşıdığı şey METİN —
 * başlık, fiyat, rozet, çağrı. Video prompt'unda bunların karşılığı yok
 * (kamera bir fiyat etiketi okumuyor), banner'da ise reçetenin yarısı bu.
 *
 * ⚠️ Bu değerlerin bir kısmı KULLANICI METNİ ve doğrudan prompt'a giriyor.
 * Şablonlar onları tırnak içinde ve "exactly" yönergesiyle kullanmak zorunda:
 * tırnaksız bırakılan bir kullanıcı metni model tarafından yönerge sanılır.
 * Çıktı bir görsel olduğu için en kötü sonuç kötü bir banner — ama o da
 * harcanmış bir kredi.
 */
export const BANNER_TEMPLATE_PLACEHOLDERS = [
  'productTitle',
  'headline',
  'badge',
  'price',
  'comparePrice',
  'ctaText',
  'brandColor',
  'brandTone',
  'industry',
] as const;
export type BannerTemplatePlaceholder = (typeof BANNER_TEMPLATE_PLACEHOLDERS)[number];

/**
 * 🔴 METİN yer tutucuları — görselin ÜSTÜNE BASILAN, tırnak içinde geçen.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Adım 7'nin kuralı banner'da YETMİYOR
 * ═══════════════════════════════════════════════════════════════════════════
 * Video tarafında boş bir yer tutucu satırdan silinip noktalama düzeltiliyor
 * ve bu yeterliydi: `The mood is {{brandTone}}.` → `The mood is.` → düzeltilir.
 *
 * Banner reçetesi ise metni TIRNAK İÇİNDE veriyor, çünkü model onu birebir
 * basmak zorunda:
 *
 *     Add a badge reading exactly "{{badge}}".
 *
 * Rozet boşsa geriye `Add a badge reading exactly "".` kalıyor. Noktalama
 * temiz, boşluk temiz, hiçbir doğrulama kırılmıyor — ve model boş tırnağı bir
 * yönerge sanıp banner'a rastgele bir rozet basıyor ya da tırnağın kendisini
 * çiziyor. Sessiz, pahalı, kullanıcının kredisinden.
 *
 * Çözüm: boş METİN yer tutucusu, içinde bulunduğu CÜMLEYİ düşürüyor. Kalan
 * yönerge daha az şey söylüyor ama söylediği doğru.
 *
 * Bu ayrım stil yer tutucularına UYGULANMIYOR (`brandColor`, `brandTone`,
 * `industry`): onlar tırnaksız birer sıfat ve boş kaldıklarında cümle hâlâ
 * anlamlı — "kurumsal bir zeminde" yerine "bir zeminde". Marka tonu yok diye
 * bütün kompozisyon cümlesini atmak, reçetenin yarısını silerdi.
 */
export const BANNER_TEXT_PLACEHOLDERS = [
  'productTitle',
  'headline',
  'badge',
  'price',
  'comparePrice',
  'ctaText',
] as const satisfies readonly BannerTemplatePlaceholder[];
export type BannerTextPlaceholder = (typeof BANNER_TEXT_PLACEHOLDERS)[number];

function isTextPlaceholder(key: string): key is BannerTextPlaceholder {
  return (BANNER_TEXT_PLACEHOLDERS as readonly string[]).includes(key);
}

/**
 * Reçeteyi cümlelere böler.
 *
 * Nokta + boşluk (ya da dize sonu) — prompt'lar düz İngilizce cümlelerden
 * oluşuyor. Ondalık sayı ("1.5") bölünmesin diye noktanın ardından boşluk
 * ARANIYOR; `1.5x` gibi bir yazım tek parça kalıyor.
 */
function splitSentences(template: string): string[] {
  return template.split(/(?<=\.)\s+/).filter((part) => part.trim().length > 0);
}

/**
 * Banner şablonu — prompt REÇETESİ, çıktının kendisi değil.
 *
 * Video şablonuyla aynı desen ve aynı gerekçe (`video.ts` → `VideoTemplate`):
 * dize + kapalı yer tutucu kümesi doğrulanabilir ve genişlemesi bedava;
 * alan alan modellenmiş bir düzen her yeni fikirde şema değişikliği isterdi.
 */
export interface BannerTemplate {
  /// Okunur ve KALICI kimlik — `inputParams.studio.banner.templateId` buna
  /// referans veriyor. Değiştirmek geçmiş işlerin kaynağını koparır.
  id: string;
  category: BannerTemplateCategory;
  /**
   * Bu düzenin anlamlı olduğu formatlar.
   *
   * Yatay bir "geniş şerit" düzeni `story` (9:16) oranında çalışmıyor; boş
   * bırakmak yerine yazıyoruz ki arayüz seçeneği kapatabilsin ve kullanıcı
   * çalışmayacak bir kombinasyona kredi yakmasın.
   */
  formats: readonly CreativeFormatId[];
  /// Ürün fotoğrafı ZORUNLU mu. `false` olan şablonlar tipografik/soyut
  /// düzenler — ürünü olmayan bir marka duyurusu da üretilebilmeli.
  requiresProductImage: boolean;
  /// Düzende başlık için yer var mı. `false` ise kullanıcının yazdığı başlık
  /// SESSİZCE YOK SAYILMIYOR — arayüz alanı hiç göstermiyor (uç bu bayrağı
  /// döndürüyor) ve servis dolu gelirse reddediyor.
  supportsHeadline: boolean;
  /// Fiyat / üstü çizili fiyat basılabiliyor mu.
  supportsPrice: boolean;
  /// Köşe rozeti ("%50", "YENİ", "SON 3 GÜN").
  supportsBadge: boolean;
  /// Çağrı düğmesi metni.
  supportsCta: boolean;
  /// Sağlayıcıya giden yönerge. `{{…}}` yer tutucuları çözülüyor.
  promptTemplate: string;
  /// İstenmeyenler. Sağlayıcının varsayılanını EZİYOR.
  negativePrompt?: string;
  /// Arayüz metinleri. Kaynak burada, çeviri `messages/*.json`da.
  name: { tr: string; en: string };
  description: { tr: string; en: string };
}

/**
 * ŞABLON DOĞRULAMASI — katalog ultracode ile üretildi.
 *
 * "Ajan doğruladım dedi" bir doğrulama değil: `banner-templates.spec.ts` her
 * kaydı bu şemadan ve `renderBannerPrompt`ten geçiriyor, tıpkı Adım 7'nin
 * `video-templates.spec.ts`i gibi.
 */
export const bannerTemplateSchema = z
  .object({
    id: z
      .string()
      .min(3)
      .max(60)
      // Kimlik i18n anahtarında ve URL'de geçiyor; boşluk ve büyük harf
      // ikisinde de kaçış gerektirirdi.
      .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'kebab-case olmalı'),
    category: z.enum(BANNER_TEMPLATE_CATEGORIES),
    formats: z.array(creativeFormatIdSchema).min(1),
    requiresProductImage: z.boolean(),
    supportsHeadline: z.boolean(),
    supportsPrice: z.boolean(),
    supportsBadge: z.boolean(),
    supportsCta: z.boolean(),
    promptTemplate: z.string().trim().min(60).max(1800),
    negativePrompt: z.string().trim().max(400).optional(),
    name: z.object({ tr: z.string().trim().min(2).max(60), en: z.string().trim().min(2).max(60) }),
    description: z.object({
      tr: z.string().trim().min(10).max(220),
      en: z.string().trim().min(10).max(220),
    }),
  })
  .superRefine((template, ctx) => {
    // Bilinmeyen yer tutucu SESSİZ KALMAZ: `{{fiyat}}` yazan bir şablon
    // prompt'a olduğu gibi girer ve model küme parantezini metin sanar.
    const used = new Set<string>();
    for (const match of template.promptTemplate.matchAll(/\{\{(\w+)\}\}/g)) {
      const key = match[1] as string;
      used.add(key);
      if (!BANNER_TEMPLATE_PLACEHOLDERS.includes(key as BannerTemplatePlaceholder)) {
        ctx.addIssue({
          code: 'custom',
          path: ['promptTemplate'],
          message: `Bilinmeyen yer tutucu: {{${key}}}`,
        });
      }
    }

    /**
     * 🔴 BAYRAK İLE REÇETE ÇELİŞEMEZ.
     *
     * `supportsPrice: true` diyen ama `{{price}}` kullanmayan bir şablon,
     * arayüzde fiyat alanını AÇAR ve kullanıcının girdiği fiyat hiçbir yere
     * gitmez. Ters yönü daha kötü: `{{badge}}` kullanan ama
     * `supportsBadge: false` diyen bir şablonda arayüz alanı hiç göstermez,
     * yer tutucu her zaman boş çözülür ve düzenin rozet köşesi boş kalır.
     * İkisi de sessiz — hata yok, log yok, yalnız kötü bir banner.
     */
    const pairs: [boolean, BannerTemplatePlaceholder, string][] = [
      [template.supportsHeadline, 'headline', 'supportsHeadline'],
      [template.supportsPrice, 'price', 'supportsPrice'],
      [template.supportsBadge, 'badge', 'supportsBadge'],
      [template.supportsCta, 'ctaText', 'supportsCta'],
    ];
    for (const [flag, placeholder, flagName] of pairs) {
      if (flag && !used.has(placeholder)) {
        ctx.addIssue({
          code: 'custom',
          path: [flagName],
          message: `${flagName}: true ama reçetede {{${placeholder}}} yok`,
        });
      }
      if (!flag && used.has(placeholder)) {
        ctx.addIssue({
          code: 'custom',
          path: [flagName],
          message: `Reçetede {{${placeholder}}} var ama ${flagName}: false`,
        });
      }
    }

    // Ürün fotoğrafı gerektirmeyen bir şablon ürün başlığını yine
    // kullanabilir (tipografik düzen); tersi ise tutarsız: fotoğrafı zorunlu
    // kılıp üründen hiç söz etmeyen bir reçetenin ürünle işi yok.
    if (template.requiresProductImage && !used.has('productTitle')) {
      ctx.addIssue({
        code: 'custom',
        path: ['requiresProductImage'],
        message: 'Ürün fotoğrafı zorunlu ama reçetede {{productTitle}} yok',
      });
    }

    /**
     * 🔴 CÜMLE BAŞINA EN FAZLA BİR METİN YER TUTUCUSU.
     *
     * `renderBannerPrompt` boş bir metin yer tutucusunun cümlesini
     * DÜŞÜRÜYOR. İki metin yer tutucusu aynı cümleye konursa biri boş kaldığı
     * anda diğeri de gider:
     *
     *     Print "{{price}}" struck through with "{{comparePrice}}".
     *
     * İndirimsiz bir üründe `comparePrice` boş → cümle düşer → FİYAT DA
     * BASILMAZ. Kullanıcı fiyat göstermeyi açmıştır ve banner'da fiyat
     * yoktur; hiçbir hata da çıkmaz.
     */
    for (const sentence of splitSentences(template.promptTemplate)) {
      const textKeys = [...sentence.matchAll(/\{\{(\w+)\}\}/g)]
        .map((match) => match[1] as string)
        .filter(isTextPlaceholder);
      if (new Set(textKeys).size > 1) {
        ctx.addIssue({
          code: 'custom',
          path: ['promptTemplate'],
          message: `Tek cümlede birden çok metin yer tutucusu: ${[...new Set(textKeys)].join(', ')}`,
        });
      }
    }

    /**
     * 🔴 BOŞ GİRDİDE AYAKTA KALAN BİR İSKELET OLMALI.
     *
     * Kullanıcı yalnız ürünü seçip hiçbir metin yazmayabilir. Her cümlesi bir
     * metin yer tutucusu taşıyan bir reçete o durumda TAMAMEN düşer ve
     * sağlayıcıya boş bir prompt gider — 4 kredi karşılığında rastgele bir
     * görsel. Reçetenin düzeni anlatan, yer tutucusuz bir omurgası olmak
     * zorunda.
     */
    const skeleton = renderBannerPrompt(template.promptTemplate, {});
    if (skeleton.length < 60) {
      ctx.addIssue({
        code: 'custom',
        path: ['promptTemplate'],
        message:
          'Tüm metin yer tutucuları boşken geriye kullanılabilir bir yönerge kalmıyor ' +
          `(${skeleton.length} karakter). Düzeni anlatan yer tutucusuz cümleler ekleyin.`,
      });
    }
  });

/**
 * Banner yer tutucularını çözer.
 *
 * `renderVideoPrompt` ile AYNI motoru (`renderPromptPlaceholders`) kullanıyor
 * ve bu bilinçli: "boş değerde yer tutucu satırdan SİLİNİR, boş dizeye
 * çevrilmez" kuralı iki katalogda da geçerli ve iki kopya hâlinde yazılsaydı
 * biri düzeltildiğinde diğeri eski kalırdı. Kural Adım 7'de ölçülmüş bir
 * hata sınıfından doğdu: `The mood is {{brandTone}}.` boş değerde
 * `The mood is.` bırakıyor ve model onu bir yönerge sanıyor.
 */
export function renderBannerPrompt(
  template: string,
  values: Partial<Record<BannerTemplatePlaceholder, string | null>>,
): string {
  const kept = splitSentences(template).filter((sentence) => {
    for (const match of sentence.matchAll(/\{\{(\w+)\}\}/g)) {
      const key = match[1] as string;
      if (!isTextPlaceholder(key)) continue;
      // Boş METİN yer tutucusu → cümle düşüyor (gerekçe
      // `BANNER_TEXT_PLACEHOLDERS` başlığında).
      if (!values[key]?.trim()) return false;
    }
    return true;
  });

  return renderPromptPlaceholders(kept.join(' '), values, BANNER_TEMPLATE_PLACEHOLDERS);
}

export const bannerParamsSchema = z.object({
  /// `BANNER_TEMPLATES` kataloğundan. Yoksa şablonsuz (brief'ten serbest).
  templateId: z.string().trim().max(80).optional(),
  /// Basılacak başlık. Şablon `supportsHeadline: false` ise REDDEDİLİYOR.
  headline: z.string().trim().max(80).optional(),
  badge: z.string().trim().max(24).optional(),
  ctaText: z.string().trim().max(28).optional(),
  /**
   * Fiyat basılsın mı.
   *
   * Fiyatın KENDİSİ istekte YOK — katalogdan, üretim anındaki değeriyle
   * okunuyor ve `StudioProductSnapshot`a yazılıyor. Kullanıcının elle fiyat
   * yazmasına izin vermek, mağazadakinden farklı bir fiyatı reklama basmanın
   * en kolay yolu olurdu.
   */
  showPrice: z.boolean().default(false),
  /// Üstü çizili fiyat da basılsın mı (`showPrice` olmadan anlamsız).
  showComparePrice: z.boolean().default(false),
  /// Marka logosunun S3 anahtarı — yüklenmiş varlık.
  logoAssetKey: z.string().max(400).optional(),
});
export type BannerParams = z.infer<typeof bannerParamsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// 2K büyütme
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Büyütme hedefi — UZUN KENAR piksel cinsinden.
 *
 * Bugün tek değer (`2k`) ve bu bilinçli: plan "2K büyütme" diyor, 4K
 * demiyor. Tek elemanlı bir liste garip görünüyor ama alternatifi düz bir
 * boolean'dı ve o, 4K eklendiği gün sözleşme kırardı. Çarpan tablosu da
 * anahtar başına yazıldığı için yeni hedef eklemek tek satır — ama fiyatı
 * ÖLÇÜLMEDEN eklenmemeli.
 */
export const UPSCALE_TARGETS = { '2k': 2048 } as const;
export type UpscaleTarget = keyof typeof UPSCALE_TARGETS;
export const upscaleTargetSchema = z.enum(['2k']);

/**
 * Sağlayıcının kabul ettiği büyütme çarpanı tavanı.
 *
 * 4× üstünde topaz çıktısı 24 MP eşiğini aşabiliyor ve fiyat $0,08'den
 * $0,16'ya çıkıyor — kredi kademesi 24 MP altını varsayıyor. Kaynak çok
 * küçükse (ör. 320 px) hedefe tek adımda ulaşılamıyor; o durumda ulaşılan
 * boyut kullanıcıya BİLDİRİLİYOR, sessizce daha küçük bir çıktı verilmiyor.
 */
export const UPSCALE_MAX_FACTOR = 4;

export const upscaleParamsSchema = z.object({
  target: upscaleTargetSchema.default('2k'),
  /**
   * Yüz iyileştirme.
   *
   * Sağlayıcının varsayılanı AÇIK ve bu ürün fotoğraflarında zarar veriyor:
   * mankensiz bir çanta görselinde "yüz" bulmaya çalışan model dokuyu
   * bozuyor. Varsayılanımız KAPALI; manken içeren görsellerde kullanıcı
   * açıyor. Sağlayıcı varsayılanını devralmak, sessizce bozulmuş bir çıktı
   * demekti — Adım 7'deki `generate_audio` tuzağının aynısı.
   */
  faceEnhancement: z.boolean().default(false),
});
export type UpscaleParams = z.infer<typeof upscaleParamsSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Ürün anlık görüntüsü
// ═══════════════════════════════════════════════════════════════════════════

/**
 * 🔴 KATALOG SATIRI KOPYALANIYOR, REFERANS VERİLMİYOR.
 *
 * `OrderLine`ın başlık/SKU/fiyat kopyalamasıyla aynı gerekçe: banner ÜRETİM
 * ANININ fiyatını basıyor. Ürün silinse, fiyatı değişse ya da mağaza
 * bağlantısı kaldırılsa bile üretilmiş görselin üstündeki rakamın nereden
 * geldiği okunabilir kalmalı. Referans verseydik altı ay sonra "bu banner'da
 * neden 249 TL yazıyor" sorusunun cevabı olmazdı.
 *
 * `productId` yine de saklanıyor — kopya, izi koparmak için değil,
 * dondurmak için.
 */
export const studioProductSnapshotSchema = z.object({
  productId: z.uuid(),
  title: z.string().max(400),
  /**
   * Kapak görselinin platform URL'i. `null` = ürünün hiç görseli yok ve ürün
   * fotoğrafı isteyen şablonlar bu üründe çalıştırılamaz.
   *
   * 🔴 `z.url()` DEĞİL, `https` ZORUNLU: bu adresi worker indiriyor ve
   * doğrulanmamış bir şema (`file:`, `http://169.254.169.254/…`) worker'ı
   * kendi ağının içine istek atmaya ikna edebilirdi. Adres mağaza
   * platformundan geliyor, yani bizim ürettiğimiz bir değer değil.
   */
  imageUrl: z
    .string()
    .max(2000)
    .refine((value) => value.startsWith('https://'), 'yalnız https adresi')
    .nullable(),
  vendor: z.string().max(200).nullable(),
  productType: z.string().max(200).nullable(),
  /// En düşük varyant fiyatı, mağazanın ANA biriminde. `null` = varyant yok.
  price: z.number().nullable(),
  /// Üstü çizili fiyat (indirim). `null` = indirim yok.
  compareAtPrice: z.number().nullable(),
  /**
   * 🔴 Para birimi — `StoreConnection.currency`den, anlık görüntüye
   * DONDURULMUŞ.
   *
   * `ProductVariant.price` yalnız bir sayı; birimi bağlantıda duruyor.
   * Anlık görüntüye almasaydık banner'a birimsiz bir rakam basılırdı
   * ("249,90") ve o rakam Türk lirası mı euro mu, banner'a bakan kimse
   * bilemezdi. Fiyatı basmaya karar veren bir özellikte bu bir ayrıntı
   * değil, doğruluk meselesi.
   */
  currency: z.string().min(3).max(3),
  /// Anlık görüntünün alındığı an — fiyatın "ne zamanki fiyat" olduğu.
  capturedAt: z.iso.datetime(),
});
export type StudioProductSnapshot = z.infer<typeof studioProductSnapshotSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Üretim parametreleri
// ═══════════════════════════════════════════════════════════════════════════

/**
 * `Creative.inputParams.studio` — worker BURADAN okuyor.
 *
 * Kuyruk yüküne KOPYALANMIYOR (`render.contract.ts` başlığındaki gerekçe:
 * kuyruk bir tetik, veri deposu değil). Alan hem üretimin girdisi hem de
 * altı ay sonra "bu görsel hangi ayarlarla üretildi" sorusunun cevabı.
 */
export const studioParamsSchema = z.object({
  mode: studioModeSchema,
  /// Üretim anında dondurulmuş katalog satırı. Katalogdan seçilmeyen
  /// (yüklenmiş fotoğrafla yapılan) üretimlerde yok.
  product: studioProductSnapshotSchema.optional(),
  tryon: tryonParamsSchema.optional(),
  banner: bannerParamsSchema.optional(),
  upscale: upscaleParamsSchema.optional(),
});
export type StudioParams = z.infer<typeof studioParamsSchema>;

/**
 * Moda ait alt nesnenin DOLDURULMUŞ hâli.
 *
 * `studioParamsSchema` üç alt nesneyi de opsiyonel bırakıyor çünkü tek şema
 * üç moda hizmet ediyor. Modun kendi nesnesinin dolu olduğunu garanti eden
 * yer `creative.cost.ts` (`resolveStudioParams`) — servis katmanına dağılmış
 * `studio.banner ?? {}` yazmak aynı sorunun ikinci cevabı olurdu.
 */
export interface ResolvedStudioParams extends StudioParams {
  mode: StudioMode;
}

/**
 * Mod başına varsayılanları oturtur.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden `creative.cost.ts`te DEĞİL burada
 * ═══════════════════════════════════════════════════════════════════════════
 * Video tarafında kardeşi (`resolveVideoParams`) API'de duruyor ve worker
 * aynı kuralı KENDİ İÇİNDE tekrar yazıyor ("ikiz değil kardeş" notu
 * `render.service.ts`te). O tekrar orada kabul edilmişti çünkü tek bir
 * sayıydı (tipe göre varsayılan süre).
 *
 * Burada üç mod ve üç alt nesne var. Aynı kuralı iki yerde yazmak, worker'ın
 * `inputParams.studio.upscale` eksik bir satırda hedefi ÇÖZMEMESİ demekti:
 * API 6 kredi kesmiş olurdu (2K hedefiyle) ve worker hedefsiz kalıp işi
 * reddederdi. Fonksiyon saf (Nest bağımlılığı yok, fırlatmıyor), yani
 * shared'e girmesinin bir maliyeti de yok.
 *
 * `creative.cost.ts` onu yeniden ihraç ediyor — API çağrı yerleri değişmedi.
 */
export function resolveStudioParams(input: StudioParams): ResolvedStudioParams {
  return {
    ...input,
    ...(input.mode === 'tryon' ? { tryon: input.tryon ?? tryonParamsSchema.parse({}) } : {}),
    ...(input.mode === 'banner' ? { banner: input.banner ?? bannerParamsSchema.parse({}) } : {}),
    ...(input.mode === 'upscale'
      ? { upscale: input.upscale ?? upscaleParamsSchema.parse({}) }
      : {}),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// Kredi kademesi
// ═══════════════════════════════════════════════════════════════════════════

/**
 * MOD ÇARPANI — tabanı `CREDIT_COST_BY_CREATIVE_TYPE` veriyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden yalnız büyütmede 1'den farklı
 * ═══════════════════════════════════════════════════════════════════════════
 * Ölçülen ham maliyetler ve 1 kredi ≈ $0,15 (Adım 7'nin onaylı tablosundan
 * türetildi: 25 kredi / $0,67 × 5,6 marj) ile:
 *
 *   deneme   $0,0475 → AD_IMAGE tabanı 4 kredi ≈ $0,60 → **12,6×**
 *   banner   $0,039  → AD_IMAGE tabanı 4 kredi ≈ $0,60 → **15,4×**
 *   büyütme  $0,08   → RESIZED  tabanı 2 kredi ≈ $0,30 → **3,75×**  ← ince
 *
 * İlk ikisi mevcut tabana OLDUĞU GİBİ oturuyor; onlara çarpan koymak, hiçbir
 * şeyi karşılamayan bir zam olurdu. Büyütme oturmuyor: 3,75× katalogdaki en
 * ince marj (UGC videosunun 4,0× tabanının bile altında) ve sağlayıcı
 * fiyatındaki küçük bir artış onu zarara çevirir.
 *
 * ×3 kullanıcıya soruldu ve onaylandı (13 Ağu 2026) → 6 kredi ≈ $0,90 →
 * **11,3×**, görsel kademesinin geri kalanıyla aynı hizada.
 *
 * 🔴 Manken ÜRETİLDİĞİNDE denemenin ham maliyeti $0,0475 → ~$0,10'a çıkıyor
 * (önce bir FLUX görseli, sonra deneme) ve marj 12,6× → **6,3×** düşüyor.
 * Buna AYRI bir çarpan KONMADI: 6,3× hâlâ onaylı kademedeki en ince marjın
 * (UGC 4,0×) üstünde, ve "manken fotoğrafı yüklemediğin için %50 zam" demek
 * tek bir kutucuğu fiyat kararına çevirirdi. Marj daralırsa çözüm tek satır —
 * ama o gün `StudioCostBreakdown`a da bir alan eklenmeli: fiyatın neden
 * değiştiği arayüzde görünmeden zam yapılmaz.
 */
export const STUDIO_CREDIT_MULTIPLIER: Record<StudioMode, number> = {
  tryon: 1,
  banner: 1,
  upscale: 3,
};

/**
 * Stüdyo maliyet dökümü — `POST /creatives/estimate` yanıtındaki `studio`.
 *
 * Video dökümüyle aynı gerekçe: yalnız toplamı gönderip "neden 6 kredi"
 * sorusunu arayüzde yeniden hesaplatmak, ikinci bir fiyat mantığı yazmak
 * demekti.
 */
export interface StudioCostBreakdown {
  mode: StudioMode;
  baseCredits: number;
  modeMultiplier: number;
  /// Büyütmede hedef uzun kenar; diğer modlarda `null`.
  targetLongEdge: number | null;
  /// Denemede manken üretilecek mi — kullanıcı bunu üretimden önce görmeli
  /// (fiyat değişmiyor ama SÜRE değişiyor: zincir iki sağlayıcı çağrısı).
  generatesModel: boolean;
}

// ═══════════════════════════════════════════════════════════════════════════
// Toplu üretim
// ═══════════════════════════════════════════════════════════════════════════

/**
 * TEK SEFERDE EN FAZLA KAÇ KALEM — kullanıcıya soruldu, 20 seçildi.
 *
 * `MAX_VARIANTS` (4) ile karıştırılmamalı: orası AYNI girdiden kaç farklı
 * sonuç, burası KAÇ FARKLI girdi. İkisi bir arada anlamsız olurdu (20 ürün ×
 * 4 varyant = 80 iş) ve toplu üretim bilerek varyant almıyor: kalem başına
 * tek çıktı.
 *
 * 20'nin gerekçesi ölçüm: 20 × 4 kredi = 80 kredi (~$12) tek tıkta taahhüt,
 * ve render kuyruğu eşzamanlılık 2 ile ~4 dakika. 50'de taahhüt $30'a,
 * kuyruk 10 dakikaya çıkıyordu — o süre boyunca tekil üretimler sırada
 * beklerdi.
 */
export const BULK_MAX_ITEMS = 20;

/**
 * Toplu üretim isteği.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 `confirmCredits` — gördüğü tutarı geri yollamak
 * ═══════════════════════════════════════════════════════════════════════════
 * Tekil üretimde `confirmRecharge: boolean` yetiyordu: tutar tek kalemlik ve
 * kart üstünde yazılı. Toplu üretimde tutar 20 kalemin toplamı ve kalemler
 * arasında değişebiliyor (fiyatı olmayan ürün, görseli olmayan ürün elenir).
 * Boolean bir onay, kullanıcının ekranda 48 kredi görüp 80 kredi ödemesine
 * izin verirdi.
 *
 * Bu yüzden istemci `/creatives/estimate/bulk`ta gördüğü TOPLAMI geri
 * gönderiyor ve sunucu kendi hesabıyla karşılaştırıyor. Tutmazsa üretim
 * BAŞLAMIYOR — 400 dönüyor ve arayüz yeni tutarı gösteriyor.
 */
const bulkBaseSchema = z.object({
  mode: studioModeSchema,
  /**
   * Hedef kimlikleri.
   *
   * `banner` / `tryon` → **ürün** kimlikleri (katalogdan).
   * `upscale`          → **kreatif** kimlikleri (kütüphaneden).
   *
   * Tek alan çünkü ikisi de UUID ve isteğin modu zaten hangisi olduğunu
   * söylüyor. İki ayrı alan (`productIds` / `creativeIds`) açsaydık her
   * istekte biri boş olurdu ve "ikisi de dolu" hâli için üçüncü bir kural
   * yazmak gerekirdi.
   */
  targetIds: z.array(z.uuid()).min(1).max(BULK_MAX_ITEMS),
  banner: bannerParamsSchema.optional(),
  tryon: tryonParamsSchema.optional(),
  upscale: upscaleParamsSchema.optional(),
  /// Ortak marka ayarları — kalem başına değil, parti başına.
  brief: z.string().trim().max(CREATIVE_BRIEF_MAX).optional(),
  brandTone: z.string().trim().max(200).optional(),
  brandColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  /// Banner formatı. `upscale` ve `tryon` modlarında anlamsız.
  formatId: creativeFormatIdSchema.optional(),
});

/// Aynı ürün iki kez seçilirse kullanıcı iki özdeş banner için iki kez ödeme
/// yapardı ve bunu ancak kütüphanede görürdü.
const uniqueTargets = <T extends { targetIds: string[] }>(input: T): boolean =>
  new Set(input.targetIds).size === input.targetIds.length;
const UNIQUE_TARGETS_ISSUE = {
  message: 'targetIds tekrarlı kimlik taşıyamaz',
  path: ['targetIds'],
};

export const bulkGenerateSchema = bulkBaseSchema
  .extend({
    /// Kullanıcının ekranda gördüğü toplam. Sunucunun hesabıyla tutmazsa
    /// üretim başlamıyor.
    confirmCredits: z.number().int().min(1),
  })
  .refine(uniqueTargets, UNIQUE_TARGETS_ISSUE);
export type BulkGenerateInput = z.infer<typeof bulkGenerateSchema>;

/// Toplu tahmin — `confirmCredits` YOK: tahmin ucunun görevi zaten o sayıyı
/// ÜRETMEK. Aynı gövdeyi iki uca gönderen bir arayüzde alanı opsiyonel
/// bırakmak da mümkündü ama o, "onay göndermeyi unuttum" hatasını tahmin
/// ucunda görünmez yapardı.
export const bulkEstimateSchema = bulkBaseSchema.refine(uniqueTargets, UNIQUE_TARGETS_ISSUE);
export type BulkEstimateInput = z.infer<typeof bulkEstimateSchema>;

/**
 * Elenen kalem.
 *
 * 🔴 SESSİZ DÜŞME YASAK: 20 ürün seçip 14 banner almak, hiçbir açıklama
 * olmadan "eksik çalışan bir özellik" gibi görünür. Elenen her kalem
 * gerekçesiyle dönüyor ve arayüz onu listelemek zorunda.
 */
export interface BulkSkippedItem {
  targetId: string;
  /// Çeviri anahtarının son parçası — arayüz `creative.bulk.skipped.<reason>`
  /// okuyor.
  reason:
    | 'product_not_found'
    | 'product_image_missing'
    | 'creative_not_found'
    | 'creative_not_ready'
    | 'already_at_target'
    | 'unsupported_type';
  /// İnsan tarafından okunabilir ad — kullanıcı hangi ürünün elendiğini
  /// kimlikten anlayamaz.
  label: string | null;
}

export interface BulkCostEstimate {
  mode: StudioMode;
  /// Üretilecek kalem sayısı (elenenler HARİÇ).
  itemCount: number;
  unitCredits: number;
  totalCredits: number;
  available: number;
  sufficient: boolean;
  topupPath: string | null;
  skipped: BulkSkippedItem[];
  /// Kuyruk tahmini — 20 kalem eşzamanlılık 2 ile dakikalar sürüyor ve
  /// kullanıcı bunu üretime basmadan önce bilmeli.
  estimatedSeconds: number;
}

export interface BulkGenerateResponse {
  creativeIds: string[];
  jobIds: string[];
  reservedCredits: number;
  itemCount: number;
  skipped: BulkSkippedItem[];
  estimatedSeconds: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// Arayüz sözleşmesi
// ═══════════════════════════════════════════════════════════════════════════

/// `GET /creatives/studio/options` — stüdyo formunun TEK kaynağı. Şablonlar,
/// manken eksenleri, büyütme hedefleri ve sınırlar tek çağrıda; video
/// tarafındaki `/creatives/video/options` ile aynı desen ve aynı gerekçe.
export interface StudioOptionsResponse {
  modes: {
    mode: StudioMode;
    /// Bu modun ürettiği kreatif tipi — arayüz kütüphane filtresini buna
    /// göre kuruyor.
    creativeType: CreativeType;
    /// Varsayılan yapılandırmanın kredisi. Büyütmede çarpan UYGULANMIŞ hâli.
    credits: number;
    available: boolean;
    unavailableReasonKey: string | null;
    labelKey: string;
    descriptionKey: string;
  }[];
  bannerTemplates: {
    id: string;
    category: BannerTemplateCategory;
    formats: readonly CreativeFormatId[];
    requiresProductImage: boolean;
    supportsHeadline: boolean;
    supportsPrice: boolean;
    supportsBadge: boolean;
    supportsCta: boolean;
    /// Arayüz çeviriyi `messages`tan okur; kaynak metin yedek.
    labelKey: string;
    descriptionKey: string;
    name: { tr: string; en: string };
    description: { tr: string; en: string };
  }[];
  model: {
    genders: readonly ModelGender[];
    ageRanges: readonly ModelAgeRange[];
    bodyTypes: readonly ModelBodyType[];
    scenes: readonly ModelScene[];
  };
  upscale: {
    targets: { id: UpscaleTarget; longEdge: number; credits: number }[];
    maxFactor: number;
  };
  bulk: { maxItems: number };
  /// Banner düzenlerinin çalıştığı format kataloğu — arayüz `CREATIVE_FORMATS`
  /// listesini kendi içinde kopyalamasın.
  formats: readonly CreativeFormatId[];
  /**
   * SAHNE ŞABLONLARI — görsel `prompt` kipinin hazır reklam postları
   * (19 Ağu 2026).
   *
   * ═══════════════════════════════════════════════════════════════════════
   * 🔴 Neden UÇTAN geliyor, neden bundle'dan değil
   * ═══════════════════════════════════════════════════════════════════════
   * Bu katalog `apps/web/src/lib/image-prompt-templates.ts`te doğdu ve orada
   * kaldığı sürece REÇETELERİYLE BİRLİKTE her ziyaretçinin indirdiği
   * JavaScript'in içindeydi. Yanıtta dönen şey reçete DEĞİL, kartı çizmek
   * için gereken üstveri: ad, açıklama, önizleme yolu, metin yuvalarının
   * etiketleri. `prompt` ve `slot.auto` `toSceneTemplateCard` tarafından
   * BEYAZ LİSTEYLE dışarıda bırakılıyor.
   *
   * `bannerTemplates` ile aynı raf değil ve aynı yanıtta olmaları bir
   * birleştirme değil bir ekonomi: ikisi de aynı formun katalogları ve
   * arayüz o formu açarken tek istek atıyor.
   */
  sceneTemplates: SceneTemplateCard[];
  /// Sahne şablonlarının ortam/vurgu renkleri. `swatch` yalnız arayüz için;
  /// modele giden renk yönergesi sunucuda kalıyor.
  scenePalettes: ScenePaletteCard[];
  /// Galerinin sol şeridi. `all` seçeneğini arayüz kendi başına ekliyor.
  sceneCategories: SceneCategoryCard[];
}
